'use client';

import { useCallback } from 'react';

type UseGrammarTextareaHandlersProps = {
  /**
   * Split the current step at the caret: `before` stays in the current step,
   * `after` moves into a new step inserted right after it.
   */
  onSplitStep?: (before: string, after: string) => void;
  /**
   * Handle multi-line pasted text. Receives the pasted lines (split on `\n`,
   * not yet filtered) so a new step can be created per line.
   */
  onPasteMultiline?: (lines: string[]) => void;
  /**
   * Callback for when an empty step should be deleted
   */
  onDeleteEmptyStep?: () => void;
  /**
   * Callback for when text should be merged with the previous step
   * Called when backspace/delete is pressed at the beginning of a step with text
   */
  onMergeWithPreviousStep?: (currentText: string) => void;
};

/**
 * Hook for handling grammar textarea-specific keyboard events
 * Provides a simplified interface that works alongside step navigation hooks
 */
function useGrammarTextareaHandlers({
  onSplitStep,
  onPasteMultiline,
  onDeleteEmptyStep,
  onMergeWithPreviousStep,
}: UseGrammarTextareaHandlersProps) {
  /**
   * Handle keydown events in the textarea
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    const { value, selectionStart, selectionEnd } = textarea;

    // Enter (without shift): split the step at the caret into two steps.
    // Shift+Enter falls through to the textarea's default newline behavior.
    if (e.key === 'Enter' && e.shiftKey === false && onSplitStep !== undefined) {
      e.preventDefault();
      onSplitStep(value.substring(0, selectionStart), value.substring(selectionEnd));
      return;
    }

    // Handle Backspace or Delete key when textarea is empty - delete the step
    if ((e.key === 'Backspace' || e.key === 'Delete') && value === '' && onDeleteEmptyStep !== undefined) {
      e.preventDefault();
      onDeleteEmptyStep();
      return;
    }

    // Handle Backspace at the beginning of a step with text - merge with previous step
    // Only trigger if cursor is at the beginning (position 0) with no text selection
    if (e.key === 'Backspace' && selectionStart === 0 && selectionEnd === 0 && value !== '' && onMergeWithPreviousStep !== undefined) {
      e.preventDefault();
      onMergeWithPreviousStep(value);
    }
  }, [onSplitStep, onDeleteEmptyStep, onMergeWithPreviousStep]);

  /**
   * Handle paste events in the textarea.
   * Multi-line pasted text is split into a separate step per line; single-line
   * pastes fall through to the browser's default (insert at the caret).
   */
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedText = e.clipboardData.getData('text');
    // Detect line breaks across platforms (\n, \r\n, lone \r).
    if (onPasteMultiline === undefined || /[\r\n]/.test(pastedText) === false) {
      return;
    }

    e.preventDefault();
    onPasteMultiline(pastedText.split(/\r\n|\r|\n/));
  }, [onPasteMultiline]);

  return {
    handleKeyDown,
    handlePaste,
  };
}

export default useGrammarTextareaHandlers;
